import type { BannerProps } from '@neo4j-ndl/react';
import { Banner, TextLink, Typography } from '@neo4j-ndl/react';
import { OPS_EVENTS } from '@nx/analytics-service';
import { trackOpsEvent } from '@nx/ops';
import type { Instance } from '@nx/state';
import { INSTANCE_STATUS } from '@nx/state';
import type { JSX, SyntheticEvent } from 'react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';

interface OpenWithMigrationAssistantChildArgs {
  onClick: (e: SyntheticEvent) => void;
  disabled: boolean;
}
interface OpenWithMigrationAssistantProps {
  instance: Instance;
  children: (args: OpenWithMigrationAssistantChildArgs) => JSX.Element;
}

const isMigrationAssistantAccessible = (instance: Instance) => {
  switch (instance.instanceStatus) {
    case INSTANCE_STATUS.CREATING:
    case INSTANCE_STATUS.DESTROYED:
    case INSTANCE_STATUS.DESTROYING:
      return false;
    default:
      return true;
  }
};

export const OpenWithMigrationAssistant = ({ instance, children }: OpenWithMigrationAssistantProps) => {
  const navigate = useNavigate();

  return children({
    onClick: () => {
      trackOpsEvent(OPS_EVENTS.MIGRATION_READINESS_REPORT_VIEW);
      void navigate(`${instance.id}/migration-readiness-report/`);
    },
    disabled: !isMigrationAssistantAccessible(instance),
  });
};

function isTestingInstance(i: Instance) {
  return i.name.startsWith('[Testing] ');
}

export const findTestMigrations = (allInstances: Instance[], instance: Instance) =>
  allInstances.find(
    (i) =>
      isTestingInstance(i) &&
      (i.instanceStatus === INSTANCE_STATUS.RUNNING ||
        i.instanceStatus === INSTANCE_STATUS.UPDATING ||
        i.instanceStatus === INSTANCE_STATUS.PAUSING ||
        i.instanceStatus === INSTANCE_STATUS.RESUMING ||
        i.instanceStatus === INSTANCE_STATUS.PAUSED) &&
      i.clonedFrom === instance.id,
  );

export const findLiveMigrations = (allInstances: Instance[], instance: Instance) =>
  allInstances.find(
    (i) =>
      !isTestingInstance(i) &&
      (i.instanceStatus === INSTANCE_STATUS.RUNNING ||
        i.instanceStatus === INSTANCE_STATUS.UPDATING ||
        i.instanceStatus === INSTANCE_STATUS.PAUSING ||
        i.instanceStatus === INSTANCE_STATUS.RESUMING ||
        i.instanceStatus === INSTANCE_STATUS.PAUSED) &&
      i.clonedFrom === instance.id,
  );

interface MigrationAssistantBannerProps {
  type: BannerProps['type'];
  title?: BannerProps['title'];
  description: BannerProps['description'];
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const MigrationAssistantBanner = ({ title, type, description, onClick }: MigrationAssistantBannerProps) => {
  return (
    <Banner
      hasIcon
      title={title}
      description={description}
      className="m-3 mt-0"
      type={type}
      actions={[
        {
          label: 'Migration readiness report',
          onClick: onClick,
        },
      ]}
    ></Banner>
  );
};

interface MigrationAssistantBannersProps {
  instance: Instance;
  allInstances: Instance[];
}

export const MigrationAssistantBanners = ({ instance, allInstances }: MigrationAssistantBannersProps) => {
  const hasTestMigrations = useMemo(() => findTestMigrations(allInstances, instance), [allInstances, instance]);
  const hasLiveMigrations = useMemo(() => findLiveMigrations(allInstances, instance), [allInstances, instance]);

  return (
    <OpenWithMigrationAssistant instance={instance}>
      {({ onClick }) => (
        <>
          {!hasTestMigrations && !hasLiveMigrations && (
            <MigrationAssistantBanner
              title="Migration required"
              description={
                <Typography variant="body-medium">
                  AuraDB version 4 instances will reach end of support on{' '}
                  <TextLink
                    type="external"
                    href="https://neo4j.com/developer/kb/neo4j-supported-versions/#_neo4j_database_enterprise_edition_4"
                  >
                    November 30, 2025
                  </TextLink>
                  .
                </Typography>
              }
              type="warning"
              onClick={onClick}
            ></MigrationAssistantBanner>
          )}
          {hasTestMigrations && (
            <MigrationAssistantBanner
              description="Test migration is ready for evaluation."
              type="success"
              onClick={onClick}
            ></MigrationAssistantBanner>
          )}
          {hasLiveMigrations && (
            <MigrationAssistantBanner
              description="Live migration is ready for evaluation."
              type="success"
              onClick={onClick}
            ></MigrationAssistantBanner>
          )}
        </>
      )}
    </OpenWithMigrationAssistant>
  );
};
